/**
 * Hours saved split by calendar year, for the "By year" rows under the
 * Projected Hour Savings tile. The rules are the tile's own, applied per year
 * rather than in total, so the rows always add up to its headline:
 *
 *   - A completed project's ledger has a month on every entry. Months up to
 *     and including this one are realised; later months are forecast.
 *   - A pipeline project has one expected value and no ledger yet, so the
 *     whole of it lands in its go-live year - the year the Reports year filter
 *     already gives it, where a July-December due date counts as next year
 *     because savings only start after go-live.
 */
import type { Project } from '../types'
import { monthTotal } from './ledger'

export interface YearValue {
  year: number
  realised: number
  forecast: number
}

/** The year a pipeline project's savings are booked to. No due date books it
 *  to the current year rather than dropping it, as the Scorecard does. */
export function pipelineYear(project: Project, curYear: number): number {
  if (!project.dueDate) return curYear
  // The calendar parts, not `new Date`: a date-only string is UTC midnight,
  // which west of UTC falls on the day before and moves 1 July into June.
  const [year, month] = project.dueDate.slice(0, 7).split('-').map(Number)
  return month >= 7 ? year + 1 : year
}

export function valueByYear(
  completed: Project[],
  pipeline: Project[],
  curYear: number,
  curMonth: number,
): YearValue[] {
  const rows = new Map<number, YearValue>()
  const row = (year: number) => {
    let r = rows.get(year)
    if (!r) {
      r = { year, realised: 0, forecast: 0 }
      rows.set(year, r)
    }
    return r
  }

  for (const project of completed) {
    const ledger = project.monthlyLedger
    if (!ledger) continue
    for (const key of Object.keys(ledger)) {
      const year = Number(key.slice(0, 4))
      const month = Number(key.slice(5, 7))
      if (Number.isNaN(year) || Number.isNaN(month)) continue
      const hours = monthTotal(ledger, key)
      if (hours === 0) continue
      const banked = year < curYear || (year === curYear && month <= curMonth)
      if (banked) row(year).realised += hours
      else row(year).forecast += hours
    }
  }

  for (const project of pipeline) {
    const hours = project.expectedValue || 0
    if (hours === 0) continue
    row(pipelineYear(project, curYear)).forecast += hours
  }

  return [...rows.values()]
    .filter(r => r.realised !== 0 || r.forecast !== 0)
    .sort((a, b) => a.year - b.year)
}

/** The rows inside the goal window set in Settings. Either end may be unset,
 *  and with neither set every row is kept. */
export function withinWindow(rows: YearValue[], startYear?: number, endYear?: number): YearValue[] {
  return rows.filter(r => (!startYear || r.year >= startYear) && (!endYear || r.year <= endYear))
}
